import { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';

const Navbar = () => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  const [userEmail, setUserEmail] = useState('');
  const [userName, setUserName] = useState('');

  // Get user data from localStorage on component mount
  useEffect(() => {
    const userDetails = localStorage.getItem('admindetails');
    if (userDetails) {
      try {
        const userData = JSON.parse(userDetails);
        setUserEmail(userData.email || '');
        setUserName(userData.name || '');
      } catch (error) {
        console.error('Error parsing user details:', error);
      }
    }
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Function to close dropdown
  const closeDropdown = () => {
    setIsDropdownOpen(false);
  };

  // Profile icon SVG
  const ProfileIcon = () => (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
    </svg>
  );

  // Chevron icon for dropdown
  const ChevronIcon = ({ isOpen }) => (
    <motion.div
      animate={{ rotate: isOpen ? 180 : 0 }}
      transition={{ duration: 0.2 }}
      className="ml-2"
    >
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
      </svg>
    </motion.div>
  );

  return (
    <header className="bg-white shadow-sm h-[60px] px-6 flex justify-end items-center">
      {/* Right side - Profile Dropdown */}
      <div className="relative" ref={dropdownRef}>
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => setIsDropdownOpen(!isDropdownOpen)}
          className="flex items-center focus:outline-none"
        >
          <div className="w-9 h-9 rounded-full bg-indigo-100 flex items-center justify-center overflow-hidden">
            <ProfileIcon />
          </div>
          <ChevronIcon isOpen={isDropdownOpen} />
        </motion.button>

        <AnimatePresence>
          {isDropdownOpen && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.2 }}
              className="absolute right-0 mt-2 w-max px-[20px] bg-white rounded-md shadow-lg py-1 z-50 border border-gray-100"
            >
              {/* Profile section */}
              <div className="flex justify-center py-[30px] items-center border-b border-gray-100">
                <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center overflow-hidden">
                  <ProfileIcon />
                </div>
                <div className="ml-3">
                  <p className="text-[16px] font-medium text-[#1E1E1E] truncate">{userName}</p>
                  <p className="text-[14px] text-[#7F7F7F] truncate">{userEmail}</p>
                </div>
              </div>

              {/* HOST CONTROL section */}
              <div className="px-4 py-2">
                <p className="text-[16px] font-semibold text-[#1E1E1E] uppercase tracking-wider">HOST CONTROL</p>
              </div>

              {/* Dropdown items */}
              <Link 
                to="/dashboard/event-management/create-event" 
                onClick={closeDropdown}
              >
                <motion.div
                  whileHover={{ x: 5 }}
                  className="block px-4 py-4 text-[16px] text-[#1E1E1E] hover:bg-indigo-50 hover:text-indigo-600 flex items-center cursor-pointer"
                >
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                  Create event
                </motion.div>
              </Link>

              <Link 
                to="/dashboard/event-management" 
                onClick={closeDropdown}
              >
                <motion.div
                  whileHover={{ x: 5 }}
                  className="block px-4 py-4 text-[16px] text-[#1E1E1E] hover:bg-indigo-50 hover:text-indigo-600 flex items-center cursor-pointer"
                >
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                  Manage event
                </motion.div>
              </Link>

              <div className="border-t border-gray-100 my-4"></div>

              <Link 
                to="/dashboard/account-settings" 
                onClick={closeDropdown}
              >
                <motion.div
                  whileHover={{ x: 5 }}
                  className="block px-4 py-2 text-[16px] text-[#1E1E1E] hover:bg-indigo-50 hover:text-indigo-600 flex items-center cursor-pointer"
                >
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  Account settings
                </motion.div>
              </Link>

              <motion.div
                whileHover={{ x: 5 }}
                whileTap={{ backgroundColor: "rgba(239, 68, 68, 0.1)" }}
                onClick={() => {
                  closeDropdown();
                  // Add your logout logic here
                }}
                className="block px-4 py-4 text-sm text-red-600 hover:bg-red-50 flex items-center cursor-pointer"
              >
                <svg
                 className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                </svg>
                Logout
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </header>
  );
};

export default Navbar;